<template>
  <div class="completed-panel">
    <div id="completed-buttons" class="completed-panel__main">
      <!-- 同意は緑カードの上（白い面）。完成メッセージより先に読ませる -->
      <CompletedAutoRtConsent
        v-if="isAutoRtConsentVisible"
        class="completed-panel__consent"
        :info="autoRtInfo"
        :has-partner-terms="hasAutoRtPartnerTerms"
        :partner-terms-html="autoRtPartnerTermsHtml"
        :agreed="isAgree"
        @update:agreed="onRtAgreeChange"
      />

      <!-- 完成メッセージは 1 行に抑え、4 つの出力方法を最初の画面に収める -->
      <div class="completed-panel__done">
        <p class="completed-panel__done-title">
          {{ documentLabel }}が完成しました！
          <span class="completed-panel__done-lead"
            >お疲れさまでした。すぐに使えます</span
          >
        </p>
        <!-- 装飾なので alt は空。文言の右に置く -->
        <img
          class="completed-panel__done-goat"
          src="/static/renew3_img/completed/yagiphone.png"
          alt=""
          aria-hidden="true"
        />
      </div>

      <div class="completed-panel__actions">
        <!--
          4 つは同じ大きさの 2×2（PC は横 4 枚）。おすすめは枠とタイトルの塗りだけで示す。
          メール便は 9/24 の刷新で「そのほか」の 2 番目に下がり決済画面 UU が落ちたため、1 段目へ戻す。
          GTM が見ている外側の id（pdf-email / pdf-email-none / quickemail / combie / pdf-download）は変えない。
        -->
        <div class="completed-panel__grid">
          <div :id="isEmailVerified ? 'pdf-email' : 'pdf-email-none'">
            <CompletedActionCard
              :button-id="isEmailVerified ? pdfMailBtnId : 'pdf-mail-btn'"
              image-src="/static/renew2_img/mail_bnr_20231011.png"
              :title="mailAction.title"
              :note="mailAction.note"
              badge="おすすめ"
              emphasis
              :disabled="!isOnline || !mailPdfEnabled"
              @click="onMailClick"
            />
          </div>

          <div v-if="isQuickEmailVisible" id="quickemail">
            <CompletedActionCard
              button-id="quick-email-btn"
              image-src="/static/renew2_img/quickemail_new_btn_20260419.png"
              title="応募先にメール"
              :note="quickEmailLabel"
              :disabled="!isOnline || !localIsSendYagiEmailEnabled"
              @click="handleYagiEmailClick"
            />
          </div>

          <div v-if="eprintEnabled" id="combie" ref="eprintCtaEl">
            <CompletedActionCard
              button-id="pdf-eprint-btn"
              :image-src="eprintImageSrc"
              title="コンビニで印刷"
              note="全国のコンビニで印刷可能"
              @click="handleEventWithLoginCheck('show-preview-4conv')"
            />
          </div>

          <div v-if="isPdfDownloadButtonVisible" id="pdf-download">
            <CompletedActionCard
              :button-id="pdfDownloadBtnId"
              image-src="/static/renew2_img/download_bnr_20231011.png"
              title="PDFをダウンロード"
              note="端末に保存"
              :disabled="!isOnline || !downloadPdfEnabled"
              @click="handleEventWithLoginCheck('download-PDF')"
            />
          </div>
        </div>

        <p v-if="!isAuthenticated" class="completed-panel__login-note">
          ※メール送信・コンビニ印刷・ダウンロードのご利用にはログインが必要です
        </p>

        <!-- 送付状は郵送が前提なので、封筒の宛名ラベルへつなぐ -->
        <AtenaLabelSuggest
          v-if="formatId === 'letter'"
          link-id="atena-suggest-completed-letter"
          frm="completed_letter"
          lead="送付状と一緒に郵送するなら、宛名もコンビニで印刷できます"
        />

        <!-- その他のテンプレートを作った人を、ヤギディ診断（yagidia の求人・記事への入口）へつなぐ -->
        <SuggestLinkCard
          v-if="isDigramSuggestVisible"
          link-id="digram-suggest-completed"
          :href="`/yagidigram?frm=completed_${formatId}`"
          img-src="/static/img/digram/yagidyimage01.png"
          title="ヤギディ診断で、向いている仕事がわかります"
          lead="20問に答えるだけで、あなたのタイプと活躍しやすい求人がわかります"
          cta="診断してみる"
        />
      </div>
    </div>

    <div class="completed-panel__help">
      <p class="completed-panel__help-title">
        お困りの場合は以下をご参照ください
      </p>
      <a
        href="https://www.yagish.jp/information/pdf_mail_setting/"
        target="_blank"
        rel="nofollow"
        >※メールが受信できない場合</a
      >
      <a
        v-if="isPdfDownloadButtonVisible"
        href="https://www.yagish.jp/information/2022-04-06-163828/?utm_source=blogWidget"
        target="_blank"
        rel="nofollow"
        >※端末に保存できない場合</a
      >
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 完了画面の出力パネル（旧 DownloadItem.vue の置き換え）。
 * GTM のトリガーが見ている id（completed-buttons / pdf-email / pdf-email-none / combie /
 * quickemail / pdf-download / 各ボタン id）は旧実装から変えていない。
 */
import { computed, ref, toRef } from 'vue'
import CompletedActionCard from './CompletedActionCard.vue'
import CompletedAutoRtConsent from './CompletedAutoRtConsent.vue'
import AtenaLabelSuggest from '@/components/format-edit/AtenaLabelSuggest.vue'
import SuggestLinkCard from '@/components/format-edit/SuggestLinkCard.vue'
import completedDigramSuggestFormats from '@/utils/format-edit/completedDigramSuggestFormats.json'
import { useCompletedOutput } from '@/composables/format-edit/useCompletedOutput'
import { resolveCompletedDocumentLabel } from '@/utils/format-edit/completedDocumentLabel'

const props = withDefaults(
  defineProps<{
    formatId?: string
    isSendYagiEmailEnabled?: boolean
    mailPdfEnabled?: boolean
    downloadPdfEnabled?: boolean
    /** コンビニ印刷CTAを出すか。宛名L判など通常の eprint フロー非対応の用紙で false */
    eprintEnabled?: boolean
  }>(),
  {
    formatId: '',
    isSendYagiEmailEnabled: true,
    mailPdfEnabled: true,
    downloadPdfEnabled: true,
    eprintEnabled: true
  }
)

const emit = defineEmits<{
  (e: 'update:isSendYagiEmailEnabled', value: boolean): void
}>()

const eprintCtaEl = ref<HTMLElement | null>(null)

const {
  isAuthenticated,
  isEmailVerified,
  isOnline,
  isPdfDownloadButtonVisible,
  isQuickEmailVisible,
  quickEmailLabel,
  localIsSendYagiEmailEnabled,
  pdfMailBtnId,
  pdfDownloadBtnId,
  autoRtInfo,
  hasAutoRtPartnerTerms,
  autoRtPartnerTermsHtml,
  isAgree,
  handleEventWithLoginCheck,
  handleYagiEmailClick,
  requestEmailVerification,
  observeEprintCtaImpression,
  onRtAgreeChange
} = useCompletedOutput({
  formatId: toRef(props, 'formatId'),
  isSendYagiEmailEnabled: toRef(props, 'isSendYagiEmailEnabled'),
  onSendYagiEmail: () => emit('update:isSendYagiEmailEnabled', false)
})

observeEprintCtaImpression(eprintCtaEl)

const documentLabel = computed(() =>
  resolveCompletedDocumentLabel(props.formatId)
)

/** 宛名は用紙が違うので、コンビニ印刷の絵も宛名用（3 社）にする */
const eprintImageSrc = computed(() =>
  props.formatId === 'atena'
    ? '/static/renew2_img/atena_print_3stores.png'
    : '/static/renew2_img/print_20231024.png'
)

const isDigramSuggestVisible = computed(() =>
  completedDigramSuggestFormats.formatIds.includes(props.formatId)
)

const isAutoRtConsentVisible = computed(
  () => isAuthenticated.value && !!autoRtInfo.value.clientId
)

const mailAction = computed(() => {
  if (isEmailVerified.value) {
    return {
      title: 'PDFをメールで送る',
      note: '自分のメールアドレスにPDFデータを送信'
    }
  }
  if (isAuthenticated.value) {
    return {
      title: '確認メールを送る',
      note: 'メールアドレスの確認後にメールで送信できます'
    }
  }
  return {
    title: 'PDFをメールで送る',
    note: '自分のメールアドレスにPDFデータを送信'
  }
})

function onMailClick() {
  if (isEmailVerified.value) {
    handleEventWithLoginCheck('mail-PDF')
    return
  }
  if (isAuthenticated.value) {
    requestEmailVerification()
    return
  }
  handleEventWithLoginCheck('send-email-verification')
}
</script>

<style lang="scss" scoped>
$deep-green: #004d40;
$note-color: #6b6b6b;

.completed-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__main {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /*
    ステップバーとの間は全ステップ共通の .cards の margin-top（SP 1rem / PC 2rem）で空いている。
    共通側は変えず、完了画面の同意ブロックだけ半分まで引き上げる。
  */
  &__consent {
    margin-top: -0.5rem;
    padding: 0 14px;
    color: #333;
  }

  /** 完成メッセージ。ヤギと 2 行の文言を横並びで中央に置く */
  &__done {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: $deep-green;
  }

  &__done-goat {
    flex: 0 0 auto;
    width: 44px;
    height: auto;
  }

  &__done-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: bold;
    line-height: 1.35;
  }

  &__done-lead {
    display: block;
    font-size: 0.75rem;
    font-weight: normal;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /*
    SP は 2 列。宛名などで 3 枚になったときは、最後の 1 枚を横幅いっぱいにして
    1 枚だけ半端に残らないようにする。
  */
  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 8px;

    > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }

  &__login-note {
    margin: 0;
    font-size: 0.688rem;
    line-height: 1.4;
    color: $note-color;
    text-align: center;
  }

  @media (width >= 768px) {
    &__consent {
      margin-top: -1rem;
      padding: 0 24px;
    }

    &__main {
      gap: 18px;
    }

    &__done {
      gap: 12px;
    }

    &__done-goat {
      width: 64px;
    }

    &__done-title {
      font-size: 1.5rem;
    }

    &__done-lead {
      font-size: 1rem;
    }

    /* PC は横 1 列。枚数が減っても均等に分ける */
    &__grid {
      grid-template-columns: none;
      grid-auto-columns: minmax(0, 1fr);
      grid-auto-flow: column;
      gap: 12px;

      > :last-child:nth-child(odd) {
        grid-column: auto;
      }
    }
  }

  &__help {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 0.813rem;

    a {
      color: $deep-green;
      text-decoration: underline;
    }
  }

  &__help-title {
    margin: 0;
    font-weight: bold;
    color: $deep-green;
  }
}
</style>
